import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UserSearchService } from '@fi-shared/fi-shared-services';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ModuleType } from '@fi-shared/types';
import { ILightWeightUser } from '@fi-shared/models';
import { UP_ARROW, DOWN_ARROW, ENTER } from '@angular/cdk/keycodes';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'fi-text-editor-tag-users2',
    template: `
        <input
            type="text"
            class="fi-text-editor-tag-input"
            [ngModel]="searchTagString$ | async"
            [matAutocomplete]="usersAutoComplete"
        />
        <mat-autocomplete
            [class]="'fi-text-editor-tag-users-autocomplete-panel'"
            autoActiveFirstOption
            #usersAutoComplete="matAutocomplete"
            (optionSelected)="onTagUserSelected($event)"
        >
            @for (user of users$ | async; track user) {
                <mat-option [value]="user">
                    <fi-profile-icon [profileData]="user" [size]="ProfileSizes.Size32px"></fi-profile-icon>
                    <span class="user-name">{{ user.FullName }}</span>
                </mat-option>
            }
        </mat-autocomplete>
    `,
    styleUrls: ['./fi-text-editor-tag-users2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-text-editor-tag-users2' },
    standalone: false,
})
export class FiTextEditorTagUsers2Component implements OnInit, OnDestroy {
    @Input() editorElementRef: ElementRef<HTMLElement>;
    @Input() searchObjectId: number; //todo
    @Input() moduleType = ModuleType.Core; //todo
    @Output() contentChange = new EventEmitter<void>();
    @Output() newUserTagged = new EventEmitter<ILightWeightUser>();

    searchTagString$ = new Subject<string>();
    users$ = new BehaviorSubject<ILightWeightUser[]>([]);
    ProfileSizes = FiProfileSizes;

    @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) inputAutoComplete: MatAutocompleteTrigger;
    @ViewChild(MatAutocomplete, { static: true }) autocompleteComponent: MatAutocomplete;

    constructor(
        private userSearchService: UserSearchService,
        private textEditorTagUsersService: TextEditorTagUsersService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        fromEvent(this.editorElementRef.nativeElement, 'keydown').subscribe(($event: KeyboardEvent) =>
            this.onEditorKeydown($event),
        );
        fromEvent(this.editorElementRef.nativeElement, 'keyup').subscribe(($event: KeyboardEvent) =>
            this.onEditorKeyup($event),
        );

        this.userSearchService.Initialize();
        this.searchTagString$
            .pipe(takeUntil(this.ngOnDestroy$))
            .pipe(distinctUntilChanged())
            .subscribe((value) => this.onSearchTagChanged(value));
    }

    private onEditorKeydown($event: KeyboardEvent) {
        if (this.autocompleteComponent.isOpen) {
            if ([UP_ARROW, DOWN_ARROW, ENTER].indexOf($event.keyCode) !== -1) {
                $event.preventDefault();
                const options = this.autocompleteComponent.options.toArray();
                let optionIndex = options.findIndex((option) => option.active);
                if ($event.keyCode === ENTER) {
                    if (optionIndex !== -1) {
                        options[optionIndex].select();
                    }
                } else {
                    if (optionIndex !== -1) {
                        if ($event.keyCode === UP_ARROW && optionIndex > 0) {
                            //options[optionIndex].deselect();
                            options[optionIndex].setInactiveStyles();
                            optionIndex--;
                            options[optionIndex].setActiveStyles();
                        } else if ($event.keyCode === DOWN_ARROW && optionIndex < options.length - 1) {
                            //options[optionIndex].deselect();
                            options[optionIndex].setInactiveStyles();
                            optionIndex++;
                            options[optionIndex].setActiveStyles();
                        }
                    } else if (options.length) {
                        options[0].setActiveStyles();
                    }
                }
            }
        }
    }

    private onEditorKeyup($event: KeyboardEvent) {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) {
            return false;
        }

        const range = sel.getRangeAt(0);
        const nodeText = range.commonAncestorContainer.textContent;
        if (!nodeText) {
            return false;
        }

        const tagMatch = nodeText.substr(0, range.endOffset).match(/@([^\s]+)$/);
        this.searchTagString$.next(tagMatch ? tagMatch[1] : undefined);
    }

    private currentTagSearchCancel$: Subject<void>;
    private onSearchTagChanged(searchString: string) {
        if (this.currentTagSearchCancel$) {
            this.currentTagSearchCancel$.next();
            this.currentTagSearchCancel$.complete();
        }

        this.currentTagSearchCancel$ = new Subject<void>();

        if (searchString) {
            this.userSearchService
                .SearchUsers(searchString, this.moduleType, this.searchObjectId, this.currentTagSearchCancel$)
                .then((users) => {
                    this.inputAutoComplete.openPanel();
                    const firstNameMatches = users.filter(
                        (user) => user.FullName.toLowerCase().indexOf(searchString.toLowerCase()) === 0,
                    );
                    const lastNameMatches = users.filter(
                        (user) =>
                            firstNameMatches.indexOf(user) === -1 &&
                            user.FullName.toLowerCase().indexOf(' ' + searchString.toLowerCase()) !== -1,
                    );
                    this.users$.next(firstNameMatches.concat(lastNameMatches).slice(0, 5));
                });
        } else {
            this.inputAutoComplete.closePanel();
            this.users$.next([]);
        }
    }

    onTagUserSelected($event: MatAutocompleteSelectedEvent) {
        const user: ILightWeightUser = $event.option.value;
        this.textEditorTagUsersService.insertTag(this.editorElementRef.nativeElement, user, () => {
            this.contentChange.emit();
            this.newUserTagged.emit(user);
        });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
